import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from "@mui/material";
import { UserInitialStateType } from "DataTypes/Redux";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import AdminApi from "Service/Admin.api";
import { StyledTableCell } from "./ProfessionalUserList";
import { StatusChip } from "Pages/OrderHistory/OrderHistory";
import VerifiedIcon from '@mui/icons-material/Verified';
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export enum HotlineRequestStatus {
  Pending = 0,
  PaymentLinkSent = 1,
  PaymentCompleted = 2,
};

interface HotlineUser {
  id: number;
  name: string;
  mobileNumber: string;
  status: number;
}

interface ProfessionalUser {
  id: number;
  firstName: string;
  lastName: string;
  contactNumber: string;
  highestQualification: string;
}


const Hotline = () => {
    const navigate = useNavigate();
  // form states
  const [name, setName] = useState<string>("");
  const [mobile, setMobile] = useState<string>("");

  // list of submitted hotline users
  const [users, setUsers] = useState<any[]>([]);

  // modal state
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [amount, setAmount] = useState<string>("");

    const [professionals, setProfessionals] = useState<ProfessionalUser[]>([]);
    const [selectedProfessionalId, setSelectedProfessionalId] = useState<number | null>(null);

    const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );

  // fetch hotline listing
    const fetchList = async () => {
        try {
            const response = await AdminApi.HotlineList();

            if (response?.success && Array.isArray(response.data)) {
                setUsers(
                    response.data.sort((a: HotlineUser, b: HotlineUser) => b.id - a.id) // TEMP: Frontend-side sorting (newest first)
                );
            } else {
                setUsers([]);
            }
        } catch (error) {
            console.error("Error fetching hotline list:", error);
        }
    };

    const fetchProfessionals = async () => {
        try {
            const res = await AdminApi.fetchVerifyProfessionalList(); 
            if (res?.success && Array.isArray(res.data)) {
                setProfessionals(res.data);
            }
        } catch (error) {
            console.error("Error fetching professionals:", error);
        }
    };

  useEffect(() => {
    fetchProfessionals();
    fetchList();
  }, []);

const getStatusProps = (status: number) => {
    switch (status) {
        case HotlineRequestStatus.Pending:
            return {
                label: "Pending",
                className: "pending",
                icon: undefined
            };
        case HotlineRequestStatus.PaymentLinkSent:
            return {
                label: "Payment Link Sent",
                className: "sent",
                icon: undefined
            };
        case HotlineRequestStatus.PaymentCompleted:
            return {
                label: "Payment Completed",
                className: "completed",
                icon: <VerifiedIcon />
            };
        default:
            return {
                label: "Unknown",
                className: "unknown"
            };
    }
};

  // submit form
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const payload = {
    createdBy: myInfo.firstname,
    name,
    mobileNumber: mobile,
    assignedProfessionalId: selectedProfessionalId,
    createdByUserId: myInfo.id
    };

    try {
      const res = await AdminApi.CreateHotline(payload);

      if (res?.success) {
        setName("");
        setMobile("");
        setSelectedProfessionalId(null);
        fetchList();
      }
    } catch (error) {
      console.error("Error creating hotline:", error);
    }
  };

  // open modal when admin clicks “Send Payment Link”
  const openPayment = (id: number) => {
    setSelectedId(id);
    setOpenPaymentModal(true);
  };

  // send payment link
  const sendPaymentLink = async () => {
    if (!selectedId) return;

    try {
      const response = await AdminApi.sendPaymentLink({
        hotlineRequestId: selectedId,
        amount: Number(amount),
      });

      if (response?.success) {
        setOpenPaymentModal(false);
        setAmount("");
        fetchList();
      }
    } catch (error) {
      console.error("Error sending payment link:", error);
    }
  };

 const handleReset = () => {
        setName("");
        setMobile("");
        setSelectedProfessionalId(null);
 }

    return (
        <Container sx={{
            py: 4,
            width: "100%",
            maxWidth: "100% !important",
            overflowY: "auto",
            height: "100vh",
            mb: 10,
            pb: 16, 
        }}>
            {/* 3. ADDED BACK BUTTON HERE */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Button 
                    startIcon={<ArrowBackIcon />} 
                    onClick={() => navigate(-1)}
                    sx={{ fontWeight: 'bold' }}
                >
                    Back
                </Button>
            </Box>

            {/* TITLE */}
            <Typography
                variant="h5"
                fontWeight="bold"
                mb={3}
                textAlign="center"
                width="100%"
                color="primary.main"
            >
                Hotline Creation
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    mt: 4,
                }}
            >

                {/* FORM */}
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{ maxWidth: 500, mb: 4 }}
                >
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label="Name"
                                fullWidth
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label="Mobile Number"
                                fullWidth
                                required
                                value={mobile}
                                onChange={(e) => setMobile(e.target.value)}
                                inputProps={{ maxLength: 10 }}
                            />
                        </Grid>

                        {/* Assign Professional + autocomplete */}
                        <Grid size={{ xs: 12 }}>
                            <Autocomplete
                                options={professionals}
                                getOptionLabel={(pro) => `${pro.firstName} ${pro.lastName}`}
                                onChange={(event, value) =>
                                    setSelectedProfessionalId(value ? value.id : null)
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Assign Professional"
                                        required
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 6 }} >
                            <Button fullWidth type="submit" variant="contained" size="large">
                                Submit
                            </Button>
                        </Grid>

                        <Grid size={{ xs: 6 }} >
                            <Button
                                fullWidth
                                type="reset"
                                variant="contained"
                                size="large"
                                onClick={handleReset}
                            >
                                Reset
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            {/* LISTING TABLE */}
            <Paper sx={{
                width: "100%"
            }}>
                {/* <TableContainer> */}
                <TableContainer
                    sx={{
                        width: "100%",
                        maxHeight: 470,
                        overflowY: "auto",
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                    }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>ID</StyledTableCell>
                                <StyledTableCell>Name</StyledTableCell>
                                <StyledTableCell>Mobile</StyledTableCell>
                                <StyledTableCell>PaymentLinks</StyledTableCell>
                                <StyledTableCell>Assigned By</StyledTableCell>
                                <StyledTableCell>Assigned Professional Name</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell align="center">Action</StyledTableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No Hotline Users Found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                users.map((user: any, index: number) => (
                                    <TableRow key={user.id}>
                                        <TableCell>{user.id}</TableCell>
                                        <TableCell>{user.name}</TableCell>
                                        <TableCell>{user.mobileNumber}</TableCell>
                                        {/* Payment Links bind in a tag */}
                                        <TableCell>
                                            <a href={user.paymentLink} target="_blank" rel="noopener noreferrer">
                                                Payment Link
                                            </a>
                                        </TableCell>
                                        <TableCell>{user.createdByUserName}</TableCell>
                                        <TableCell>{user.assignedProfessionalName}</TableCell>
                                        {/* STATUS */}

                                        <TableCell>
                                            <StatusChip
                                                label={getStatusProps(user.status).label}
                                                className={getStatusProps(user.status).className}
                                                icon={getStatusProps(user.status).icon}
                                                size="small"
                                            />
                                        </TableCell>

                                        {/* CONDITIONAL ACTION BUTTON */}
                                        <TableCell align="center">
                                            {user.status === HotlineRequestStatus.Pending ? (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => openPayment(user.id)}
                                                >
                                                    Send Payment Link
                                                </Button>
                                            ) : user.status === HotlineRequestStatus.PaymentLinkSent ? (
                                                <p>Payment link sent</p>
                                            ) : (
                                                <p>Payment completed</p>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* PAYMENT MODAL */}
            <Dialog open={openPaymentModal} onClose={() => setOpenPaymentModal(false)}>
                <DialogTitle>Enter Amount</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        type="number"
                        label="Amount"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPaymentModal(false)}>Cancel</Button>
                    <Button variant="contained" onClick={sendPaymentLink}>
                        Send Link
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Hotline;
